import React, { useEffect, useState } from 'react';
import { webConfig } from 'webConfig';
import Autocomplete, {
    AutocompleteRenderInputParams,
} from '@material-ui/lab/Autocomplete/Autocomplete';
import { Controller, useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { AppState } from 'store';
import { LocationSelected } from 'store/signup/types';
import { useInvokeApi } from 'pages/signup/utils/useInvokeApi';
import { City, FormAutocompleteProps } from './types';
import { CityTextField } from './CityTextField';
import { CLICK_ORG_NAME_CITY_INPUT } from 'utils/clevertapEvents';
import { pushEvent } from 'utils/clevertapUtils';

const findCityEndPoint = webConfig.baseUrl + 'locations?term=';

const isZipCode = (value: string): boolean =>
    value.length === 5 && Number.isInteger(Number.parseInt(value));

export const isLocationValid = (location: LocationSelected): boolean => {
    return location.userTypedValue.length > 1;
};

const filterOptions = (x: City[]) => x;
const getOptionLabel = (c: City) => c.suggest;
const renderOption = (c: City) => (
    <span className="fs-unmask">{c.suggest}</span>
);

export const LocationAutoComplete = <Form,>({
    country,
    name,
    onClick,
    setCountry,
    style,
}: FormAutocompleteProps<Form>) => {
    const { fingerprint } = useSelector((state: AppState) => ({
        fingerprint: state.Signup.fingerprint,
    }));

    const { control, setValue, formState } = useFormContext();

    const [options, setOptions] = useState<City[]>([]);
    const [requestState, makeRequest] = useInvokeApi<City[]>();

    const error = formState.errors[name] as { message: string };

    const handleLocationSelected = (e: unknown, city: City | string | null) => {
        if (!city) return;
        if (typeof city === 'string') {
            setValue(name as string, {
                userTypedValue: city,
                city: '',
                state: '',
                zip: '',
            });
            return;
        }
        setValue(
            name as string,
            {
                userTypedValue: city.suggest,
                city: city.city,
                state: city.state,
                zip: '',
            },
            { shouldValidate: true },
        );
        pushEvent(CLICK_ORG_NAME_CITY_INPUT, {
            city_state_value: city.suggestShort,
        });
    };
    const onChangeLocation = (value: string) => {
        if (value.length > 1) {
            makeRequest(
                'GET',
                findCityEndPoint +
                    value +
                    `&user_key=${fingerprint}&country=${country.toUpperCase()}`,
            );
        }
    };
    useEffect(() => {
        if (requestState.type === 'REQUEST_SUCCESS') {
            setOptions(requestState.data.slice(0, 5));
        }
    }, [requestState]);

    return (
        <Controller
            name={name as string}
            control={control}
            render={({
                field: { onBlur, value, onChange },
                fieldState: { invalid },
            }) => {
                return (
                    <Autocomplete
                        freeSolo
                        fullWidth={true}
                        clearOnEscape
                        closeIcon={null}
                        popupIcon={null}
                        filterOptions={filterOptions}
                        options={options}
                        renderOption={renderOption}
                        getOptionLabel={getOptionLabel}
                        inputValue={value.userTypedValue}
                        onChange={handleLocationSelected}
                        style={style}
                        renderInput={({
                            InputLabelProps,
                            InputProps,
                            id,
                            inputProps,
                        }: AutocompleteRenderInputParams) => (
                            <CityTextField
                                id={id}
                                value={value.userTypedValue}
                                onChange={(e) => {
                                    const { value } = e.target;
                                    onChange({
                                        userTypedValue: value,
                                        city: '',
                                        state: '',
                                        zip: isZipCode(value) ? value : '',
                                    });
                                    onChangeLocation(value);
                                }}
                                onBlur={onBlur}
                                country={country}
                                setCountry={setCountry}
                                onClick={onClick}
                                InputLabelProps={InputLabelProps}
                                InputProps={InputProps}
                                inputProps={inputProps}
                                errorMessage={invalid ? error.message : ''}
                                invalid={invalid}
                            />
                        )}
                    />
                );
            }}
        />
    );
};
